import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import type { UpdateProfileData } from '../types/auth';
import { propertyApi, billingApi } from '../services';
import type { PlanUsageStats, Invoice } from '../services/billing';
import PasswordInput from '../components/PasswordInput';
import type { ModalConfig } from './ShopPage';

import './ProfilePage.css';

interface ProfileFormData {
  email: string;
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface FormErrors {
  email?: string;
  currentPassword?: string;
  newPassword?: string;
  confirmPassword?: string;
  general?: string;
}

const ProfilePage: React.FC = () => {
  const { user, updateProfile, isLoading, error, clearError } = useAuth();
  const [propertyCount, setPropertyCount] = useState<number | null>(null);
  const [usageStats, setUsageStats] = useState<PlanUsageStats | null>(null);
  const [loadingUsage, setLoadingUsage] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<'profile' | 'billing'>('profile');
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [modalConfig, setModalConfig] = useState<ModalConfig>({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
  });

  const closeModal = () =>
    setModalConfig((prev) => ({ ...prev, isOpen: false }));

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Form state
  const [formData, setFormData] = useState<ProfileFormData>({
    email: user?.email || '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [isEditing, setIsEditing] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // Update form data when user changes
  useEffect(() => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        email: user.email,
      }));
    }
  }, [user]);

  // Fetch property count
  useEffect(() => {
    const fetchPropertyCount = async () => {
      try {
        const data = await propertyApi.getAll();
        setPropertyCount(data.length);
      } catch (err) {
        console.error('Failed to fetch property count for profile', err);
      }
    };
    fetchPropertyCount();
  }, []);

  // Fetch plan usage statistics
  useEffect(() => {
    let isMounted = true;
    const fetchUsage = async () => {
      try {
        const stats = await billingApi.getUsage();
        if (isMounted) setUsageStats(stats);
      } catch (err) {
        console.error('Failed to fetch plan usage stats', err);
      } finally {
        if (isMounted) setLoadingUsage(false);
      }
    };
    fetchUsage();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const fetchInvoices = async () => {
      if (user?.stripeCustomerId) {
        const data = await billingApi.getInvoices();
        setInvoices(data);
      }
    };
    fetchInvoices();
  }, [user]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const translateStatus = (status: string) => {
    switch (status.toLowerCase()) {
      case 'paid':
        return 'Bezahlt';
      case 'open':
        return 'Offen';
      case 'draft':
        return 'Entwurf';
      case 'void':
        return 'Storniert';
      case 'uncollectible':
        return 'Uneinbringlich';
      default:
        return status;
    }
  };

  const handleOpenPortal = async () => {
    setActionLoading(true);
    try {
      localStorage.setItem(
        'valuera_pre_checkout_plan',
        user?.planTier || 'free',
      );
      const response = await billingApi.openPortalSession();
      if (response.url) {
        window.location.href = response.url;
      }
    } catch (error) {
      console.error('Failed to open portal', error);
      setModalConfig({
        isOpen: true,
        type: 'error',
        title: 'Fehler',
        message: (
          <>
            Fehler beim Öffnen der Abrechnungsseite. Sollte das Problem
            weiterhin bestehen, kontaktiere uns bitte unter{' '}
            <a href="mailto:office@valuera.at">office@valuera.at</a>.
          </>
        ),
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    setModalConfig({
      isOpen: true,
      type: 'confirm',
      title: 'Abonnement kündigen',
      message:
        'Möchtest du dein Abonnement wirklich kündigen? Du behältst den Zugriff auf deine Premium-Features bis zum Ende des aktuellen Abrechnungszeitraums.',
      onConfirm: async () => {
        closeModal();
        setActionLoading(true);
        try {
          await billingApi.cancelSubscription();
          setModalConfig({
            isOpen: true,
            type: 'success',
            title: 'Erfolgreich',
            message:
              'Dein Abonnement wurde erfolgreich zum Ende des aktuellen Abrechnungszeitraums gekündigt.',
            onConfirm: () => window.location.reload(),
          });
        } catch (error) {
          console.error('Failed to cancel subscription', error);
          setModalConfig({
            isOpen: true,
            type: 'error',
            title: 'Fehler',
            message: (
              <>
                Kündigung fehlgeschlagen. Bitte versuche es später noch einmal.
                Sollte das Problem weiterhin bestehen, kontaktiere uns bitte
                unter <a href="mailto:office@valuera.at">office@valuera.at</a>.
              </>
            ),
          });
          setActionLoading(false);
        }
      },
      onCancel: closeModal,
    });
  };

  // Clear messages when form data changes
  useEffect(() => {
    if (error) {
      clearError();
    }
    if (successMessage) {
      setSuccessMessage('');
    }
  }, [formData, clearError]);

  // Email validation
  const validateEmail = (email: string): string | undefined => {
    if (!email) {
      return 'Email is required';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }

    return undefined;
  };

  // Password validation
  const validatePassword = (password: string): string | undefined => {
    if (!password) {
      return 'Password is required';
    }

    if (password.length < 8) {
      return 'Password must be at least 8 characters';
    }

    // Check for mixed case, numbers, and special characters
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password);

    if (!hasUpperCase || !hasLowerCase || !hasNumbers || !hasSpecialChar) {
      return 'Password must contain uppercase, lowercase, numbers, and special characters';
    }

    return undefined;
  };

  // Current password validation (only required when changing password)
  const validateCurrentPassword = (password: string): string | undefined => {
    if (isChangingPassword && !password) {
      return 'Current password is required to change password';
    }
    return undefined;
  };

  // Confirm password validation
  const validateConfirmPassword = (
    confirmPassword: string,
    newPassword: string,
  ): string | undefined => {
    if (isChangingPassword) {
      if (!confirmPassword) {
        return 'Please confirm your new password';
      }

      if (confirmPassword !== newPassword) {
        return 'Passwords do not match';
      }
    }

    return undefined;
  };

  // Real-time validation
  const validateField = (name: string, value: string): string | undefined => {
    switch (name) {
      case 'email':
        return validateEmail(value);
      case 'currentPassword':
        return validateCurrentPassword(value);
      case 'newPassword':
        return isChangingPassword ? validatePassword(value) : undefined;
      case 'confirmPassword':
        return validateConfirmPassword(value, formData.newPassword);
      default:
        return undefined;
    }
  };

  // Handle input change with real-time validation
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear messages when user starts typing
    if (errors.general) {
      setErrors((prev) => ({ ...prev, general: undefined }));
    }
    if (successMessage) {
      setSuccessMessage('');
    }

    // Real-time validation for touched fields
    if (touched[name]) {
      const fieldError = validateField(name, value);
      setErrors((prev) => ({
        ...prev,
        [name]: fieldError,
      }));
    }

    // Special handling for confirm password when new password changes
    if (name === 'newPassword' && touched.confirmPassword) {
      const confirmError = validateConfirmPassword(
        formData.confirmPassword,
        value,
      );
      setErrors((prev) => ({
        ...prev,
        confirmPassword: confirmError,
      }));
    }
  };

  // Handle field blur (mark as touched and validate)
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setTouched((prev) => ({ ...prev, [name]: true }));

    const fieldError = validateField(name, value);
    setErrors((prev) => ({
      ...prev,
      [name]: fieldError,
    }));
  };

  // Validate entire form
  const validateForm = (): boolean => {
    const emailError = validateEmail(formData.email);
    const currentPasswordError = validateCurrentPassword(
      formData.currentPassword,
    );
    const newPasswordError = isChangingPassword
      ? validatePassword(formData.newPassword)
      : undefined;
    const confirmPasswordError = validateConfirmPassword(
      formData.confirmPassword,
      formData.newPassword,
    );

    const newErrors: FormErrors = {
      email: emailError,
      currentPassword: currentPasswordError,
      newPassword: newPasswordError,
      confirmPassword: confirmPasswordError,
    };

    setErrors(newErrors);

    // Mark relevant fields as touched
    const touchedFields: Record<string, boolean> = { email: true };
    if (isChangingPassword) {
      touchedFields.currentPassword = true;
      touchedFields.newPassword = true;
      touchedFields.confirmPassword = true;
    }
    setTouched(touchedFields);

    return (
      !emailError &&
      !currentPasswordError &&
      !newPasswordError &&
      !confirmPasswordError
    );
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const updateData: UpdateProfileData = {
        email: formData.email,
      };

      // Add password change data if changing password
      if (isChangingPassword && formData.newPassword) {
        updateData.currentPassword = formData.currentPassword;
        updateData.newPassword = formData.newPassword;
      }

      await updateProfile(updateData);

      // Reset form state on success
      setIsEditing(false);
      setIsChangingPassword(false);
      setFormData((prev) => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      }));
      setTouched({});
      setErrors({});

      setSuccessMessage(
        isChangingPassword
          ? 'Profile and password updated successfully!'
          : 'Profile updated successfully!',
      );
    } catch (err) {
      // Error is handled by the auth context
      console.error('Profile update failed:', err);
    }
  };

  // Handle edit mode toggle
  const handleEditToggle = () => {
    if (isEditing) {
      // Cancel editing - reset form
      setFormData({
        email: user?.email || '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setErrors({});
      setTouched({});
      setIsChangingPassword(false);
    }
    setIsEditing(!isEditing);
  };

  // Check if form has errors
  const hasErrors = Object.values(errors).some((error) => error !== undefined);
  const hasChanges =
    formData.email !== user?.email ||
    (isChangingPassword && formData.newPassword);

  return (
    <div className="profile-page">
      <div className="profile-container">
        <header className="profile-header">
          <h2>Profile Settings</h2>
          <p>Manage your account information and security settings</p>
        </header>

        {/* Success message */}
        {successMessage && (
          <div className="success-message" role="alert">
            {successMessage}
          </div>
        )}

        {/* Error message */}
        {(error || errors.general) && (
          <div className="error-message" role="alert">
            {error || errors.general}
          </div>
        )}

        <div className="profile-content">
          <div className="profile-grid">
            {/* Left Column: User Profile Overview Card */}
            <div className="profile-sidebar">
              <div className="stats-card">
                <h3 className="stats-header">Portfolio-Statistik</h3>
                {loadingUsage ? (
                  <div className="stats-body">
                    <p style={{ color: 'var(--Grey-2, #a7a3e2)' }}>
                      Lade Limits & Speicherplatz...
                    </p>
                  </div>
                ) : usageStats ? (
                  <div className="stats-body">
                    {/* Properties */}
                    <div className="stats-item">
                      <span className="stats-label">Aktive Immobilien</span>
                      <span className="stats-value">
                        {usageStats.properties.used} /{' '}
                        {usageStats.properties.max === Infinity
                          ? '∞'
                          : usageStats.properties.max}
                      </span>
                      <div className="stats-progress-container">
                        <div
                          className="stats-progress-bar"
                          style={{
                            width: `${usageStats.properties.max === Infinity ? 0 : Math.min(100, (usageStats.properties.used / usageStats.properties.max) * 100)}%`,
                          }}
                        ></div>
                      </div>
                      <span className="stats-subtext">
                        {usageStats.properties.max === Infinity
                          ? 'Unbegrenzt freie Slots'
                          : `${Math.max(0, usageStats.properties.max - usageStats.properties.used)} von ${usageStats.properties.max} Slots verfügbar`}
                      </span>
                    </div>

                    {/* Storage */}
                    <div className="stats-item" style={{ marginTop: '0.5rem' }}>
                      <span className="stats-label">
                        Genutzter Speicherplatz
                      </span>
                      <span className="stats-value">
                        {formatBytes(usageStats.storage.usedBytes)} /{' '}
                        {formatBytes(usageStats.storage.maxBytes)}
                      </span>
                      <div className="stats-progress-container">
                        <div
                          className="stats-progress-bar"
                          style={{
                            width: `${Math.min(100, (usageStats.storage.usedBytes / usageStats.storage.maxBytes) * 100)}%`,
                          }}
                        ></div>
                      </div>
                      <span className="stats-subtext">
                        {formatBytes(
                          Math.max(
                            0,
                            usageStats.storage.maxBytes -
                              usageStats.storage.usedBytes,
                          ),
                        )}{' '}
                        von {formatBytes(usageStats.storage.maxBytes)} frei
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="stats-body">
                    <div className="stats-item">
                      <span className="stats-label">
                        Immobilien im Portfolio
                      </span>
                      <span className="stats-value">
                        {propertyCount !== null ? propertyCount : '...'}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column: Profile details form */}
            <div className="profile-main">
              <div className="profile-tabs">
                <button
                  className={`tab-button ${activeTab === 'profile' ? 'active' : ''}`}
                  onClick={() => setActiveTab('profile')}
                >
                  Profil & Sicherheit
                </button>
                <button
                  className={`tab-button ${activeTab === 'billing' ? 'active' : ''}`}
                  onClick={() => setActiveTab('billing')}
                >
                  Abrechnung & Plan
                </button>
              </div>

              {activeTab === 'profile' && (
                <>
                  {/* Profile Information Section */}
                  <section className="profile-section">
                    <div className="section-header">
                      <h2>Profile Information</h2>
                    </div>

                    {!isEditing ? (
                      // Display mode
                      <div className="profile-display">
                        <div className="profile-field">
                          <label className="field-label">Email Address</label>
                          <div className="field-value">{user?.email}</div>
                        </div>
                      </div>
                    ) : (
                      // Edit mode (Change Password Form)
                      <form onSubmit={handleSubmit} noValidate>
                        {/* Email field (Read-only) */}
                        <div className="form-group">
                          <label className="form-label">Email Address</label>
                          <div
                            className="field-value-static"
                            style={{
                              color: 'var(--Grey-2, #a7a3e2)',
                              padding: '0.5rem 0',
                            }}
                          >
                            {user?.email}
                          </div>
                        </div>

                        {/* Password change fields */}
                        <div
                          className="password-fields"
                          style={{ marginTop: '1rem' }}
                        >
                          {/* Current password */}
                          <div className="form-group">
                            <label
                              htmlFor="currentPassword"
                              className="form-label"
                            >
                              Current Password
                            </label>
                            <PasswordInput
                              id="currentPassword"
                              name="currentPassword"
                              value={formData.currentPassword}
                              onChange={handleInputChange}
                              onBlur={handleBlur}
                              placeholder="Enter your current password"
                              disabled={isLoading}
                              autoComplete="current-password"
                              hasError={!!errors.currentPassword}
                              aria-describedby={
                                errors.currentPassword
                                  ? 'current-password-error'
                                  : undefined
                              }
                              aria-invalid={!!errors.currentPassword}
                            />
                            {errors.currentPassword && (
                              <span
                                id="current-password-error"
                                className="field-error"
                                role="alert"
                              >
                                {errors.currentPassword}
                              </span>
                            )}
                          </div>

                          {/* New password */}
                          <div className="form-group">
                            <label htmlFor="newPassword" className="form-label">
                              New Password
                            </label>
                            <PasswordInput
                              id="newPassword"
                              name="newPassword"
                              value={formData.newPassword}
                              onChange={handleInputChange}
                              onBlur={handleBlur}
                              placeholder="Enter your new password"
                              disabled={isLoading}
                              autoComplete="new-password"
                              hasError={!!errors.newPassword}
                              aria-describedby={
                                errors.newPassword
                                  ? 'new-password-error'
                                  : undefined
                              }
                              aria-invalid={!!errors.newPassword}
                            />
                            {errors.newPassword && (
                              <span
                                id="new-password-error"
                                className="field-error"
                                role="alert"
                              >
                                {errors.newPassword}
                              </span>
                            )}
                          </div>

                          {/* Confirm password */}
                          <div className="form-group">
                            <label
                              htmlFor="confirmPassword"
                              className="form-label"
                            >
                              Confirm New Password
                            </label>
                            <PasswordInput
                              id="confirmPassword"
                              name="confirmPassword"
                              value={formData.confirmPassword}
                              onChange={handleInputChange}
                              onBlur={handleBlur}
                              placeholder="Confirm your new password"
                              disabled={isLoading}
                              autoComplete="new-password"
                              hasError={!!errors.confirmPassword}
                              aria-describedby={
                                errors.confirmPassword
                                  ? 'confirm-password-error'
                                  : undefined
                              }
                              aria-invalid={!!errors.confirmPassword}
                            />
                            {errors.confirmPassword && (
                              <span
                                id="confirm-password-error"
                                className="field-error"
                                role="alert"
                              >
                                {errors.confirmPassword}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Form actions */}
                        <div className="form-actions">
                          <button
                            type="button"
                            className="cancel-button"
                            onClick={handleEditToggle}
                            disabled={isLoading}
                          >
                            Cancel
                          </button>

                          <button
                            type="submit"
                            className={`submit-button ${isLoading ? 'loading' : ''}`}
                            disabled={isLoading || !hasChanges || hasErrors}
                          >
                            {isLoading ? (
                              <>
                                <span
                                  className="loading-spinner"
                                  aria-hidden="true"
                                ></span>
                                Updating...
                              </>
                            ) : (
                              'Save Changes'
                            )}
                          </button>
                        </div>
                      </form>
                    )}
                  </section>

                  {/* Account Management Section */}
                  <section className="profile-section">
                    <div className="section-header">
                      <h2>Account Management</h2>
                    </div>

                    <div className="account-actions">
                      <div className="action-item">
                        <div className="action-info">
                          <h3>Change Password</h3>
                          <p>
                            Update your account password to keep your account
                            secure.
                          </p>
                        </div>
                        <button
                          type="button"
                          className="action-button secondary"
                          onClick={() => {
                            setIsEditing(true);
                            setIsChangingPassword(true);
                          }}
                          disabled={isEditing}
                        >
                          Change Password
                        </button>
                      </div>

                      <div className="action-item">
                        <div className="action-info">
                          <h3>Account Deactivation</h3>
                          <p>
                            Temporarily deactivate your account. You can
                            reactivate it anytime by logging in.
                          </p>
                        </div>
                        <button
                          className="action-button danger"
                          disabled={true}
                          title="Coming soon"
                        >
                          Deactivate Account
                        </button>
                      </div>
                    </div>
                  </section>
                </>
              )}

              {activeTab === 'billing' && (
                <>
                  <section className="profile-section billing-details-section">
                    <div className="section-header">
                      <h2>Abrechnungsinformationen</h2>
                    </div>
                    <div className="billing-info-card">
                      <div className="billing-info-grid">
                        <div className="info-item">
                          <span className="info-label">Aktueller Plan:</span>
                          <span className="info-value plan-badge">
                            {(user?.planTier || 'free').toUpperCase()}
                          </span>
                        </div>

                        {user?.pendingPlanTier && (
                          <div className="info-item">
                            <span className="info-label">
                              Zukünftiger Plan:
                            </span>
                            <span className="info-value pending-plan-badge">
                              {user.pendingPlanTier.toUpperCase()}
                            </span>
                          </div>
                        )}

                        {user?.cardBrand && user?.cardLast4 && (
                          <div className="info-item">
                            <span className="info-label">Zahlungsmethode:</span>
                            <span className="info-value card-details">
                              <span className="card-brand">
                                {user.cardBrand.toUpperCase()}
                              </span>{' '}
                              •••• {user.cardLast4}
                            </span>
                          </div>
                        )}

                        {user?.currentPeriodEnd && (
                          <div className="info-item">
                            <span className="info-label">
                              {user?.cancelAtPeriodEnd
                                ? 'Läuft aus am:'
                                : 'Nächste Abrechnung am:'}
                            </span>
                            <span className="info-value">
                              {formatDate(user.currentPeriodEnd)}
                            </span>
                          </div>
                        )}
                      </div>

                      <div className="billing-actions">
                        <button
                          className="secondary-button"
                          onClick={handleOpenPortal}
                          disabled={actionLoading}
                        >
                          Zahlungsdaten & Rechnungen verwalten (Stripe)
                        </button>
                        {user?.planTier &&
                          user.planTier !== 'free' &&
                          !user?.cancelAtPeriodEnd && (
                            <button
                              className="danger-link-button"
                              onClick={handleCancelSubscription}
                              disabled={actionLoading}
                            >
                              Abonnement kündigen
                            </button>
                          )}
                      </div>
                    </div>
                  </section>

                  {invoices.length > 0 && (
                    <section className="profile-section invoices-section">
                      <div className="section-header">
                        <h2>Rechnungsverlauf</h2>
                      </div>
                      <div className="table-responsive">
                        <table className="invoices-table">
                          <thead>
                            <tr>
                              <th>Datum</th>
                              <th>Rechnungsnummer</th>
                              <th>Betrag</th>
                              <th>Status</th>
                              <th>Aktion</th>
                            </tr>
                          </thead>
                          <tbody>
                            {invoices.map((invoice) => (
                              <tr key={invoice.id}>
                                <td>{formatDate(invoice.created)}</td>
                                <td>{invoice.number || 'Entwurf'}</td>
                                <td>
                                  {invoice.amountPaid.toLocaleString('de-DE', {
                                    style: 'currency',
                                    currency: invoice.currency,
                                  })}
                                </td>
                                <td>
                                  <span
                                    className={`status-badge ${invoice.status.toLowerCase()}`}
                                  >
                                    {translateStatus(invoice.status)}
                                  </span>
                                </td>
                                <td>
                                  {invoice.invoicePdf ? (
                                    <a
                                      href={invoice.invoicePdf}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="download-link"
                                    >
                                      PDF
                                    </a>
                                  ) : (
                                    '-'
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </section>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {modalConfig.isOpen && (
        <div
          className="confirm-modal-overlay"
          onClick={
            modalConfig.type !== 'confirm'
              ? modalConfig.onConfirm || closeModal
              : undefined
          }
        >
          <div
            className="confirm-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>{modalConfig.title}</h3>
            <p>{modalConfig.message}</p>
            <div className="confirm-modal-actions">
              {modalConfig.type === 'confirm' && (
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={modalConfig.onCancel || closeModal}
                >
                  Abbrechen
                </button>
              )}
              {modalConfig.type === 'confirm' ? (
                <button
                  type="button"
                  className="btn-danger"
                  onClick={modalConfig.onConfirm}
                >
                  Kündigen
                </button>
              ) : (
                <button
                  type="button"
                  className="btn-primary"
                  onClick={modalConfig.onConfirm || closeModal}
                >
                  OK
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
